import { useCallback, useEffect, useState } from 'react'

import { NetworkStatus } from '@apollo/client'
import { ActionIcon, Box, Loader, SegmentedControl, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useInView } from 'react-intersection-observer'

import { Link, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import CompanyFeatureGate from 'src/components/CompanyFeatureGate/CompanyFeatureGate'
import ExpirationIndicator from 'src/components/DataDisplay/ExpirationIndicator/ExpirationIndicator'
import TrackerIconButton from 'src/components/DataDisplay/TrackerIconButton/TrackerIconButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_EXPIRING_DOCUMENT_BY_CATEGORY } from 'src/graphql/documents.graphql'
import { ApplicantStage } from 'src/graphql/types/applicants'
import { DocumentCategory } from 'src/graphql/types/documents'
import { DQF_STEP_TYPES, StepType } from 'src/graphql/types/steps'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import IconRightArrow from 'src/icons/IconRightArrow'
import { isCategoryTracked, getActiveTrackerId } from 'src/lib/expirationTracking'
import { formatSnakeValue } from 'src/lib/formatters'
import { formatStepType } from 'src/lib/formatters'
import ExpirationDetailDrawer from 'src/pages/ExpirationsPage/components/ExpirationDetailDrawer'
import { cn } from 'src/utils'

const VIEW_OPTIONS = [
  { value: 'expiring', label: 'Expiring' },
  { value: 'missing', label: 'Missing Dates' },
]

const DocumentChecklist = ({ recruiters, onMetricClick }) => {
  const LIMIT = 50
  const [viewMode, setViewMode] = useState('expiring')
  const [trackerDrawerOpened, { open: openTrackerDrawer, close: closeTrackerDrawer }] = useDisclosure(false)
  const [selectedTrackerId, setSelectedTrackerId] = useState<string | null>(null)

  const { ref, inView } = useInView({ threshold: 0 })

  const handleOpenTracker = (trackerId: string) => {
    setSelectedTrackerId(trackerId)
    openTrackerDrawer()
  }

  const handleViewModeChange = (value: string) => {
    setViewMode(value)
  }

  // Map DocumentCategory to StepType, handling naming differences
  const mapDocumentCategoryToStepType = (
    category: DocumentCategory
  ): StepType | null => {
    // Handle CLEARING_HOUSE -> CLEARINGHOUSE conversion
    if (category === DocumentCategory.CLEARING_HOUSE) {
      return StepType.CLEARINGHOUSE
    }

    // Try direct mapping for categories that match StepType names
    const categoryAsStepType = category as unknown as StepType
    if (Object.values(StepType).includes(categoryAsStepType)) {
      return categoryAsStepType
    }

    // Return null for categories that don't have a corresponding StepType
    return null
  }

  // Check if a document category maps to a DQF step type
  const isDQFCategory = (category: DocumentCategory): boolean => {
    const stepType = mapDocumentCategoryToStepType(category)
    return stepType !== null && DQF_STEP_TYPES.includes(stepType)
  }

  const onError = useCallback(() => {
    toast('Unable to get expiring documents, please try again.', 'error')
  }, [])

  const {
    data: expiringDocuments,
    loading: loadingExpiringDocuments,
    fetchMore,
    networkStatus,
  } = useQuery(GET_EXPIRING_DOCUMENT_BY_CATEGORY, {
    variables: {
      isInfiniteScroll: true,
      filters: {
        recruiters: recruiters,
        onlyMissingDates: viewMode === 'missing',
      },
      pageInfo: { offset: 0, limit: LIMIT },
    },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  const loadedItems =
    expiringDocuments?.documentsExpirationByCategory?.items ?? []
  const totalCount =
    expiringDocuments?.documentsExpirationByCategory?.totalCount ?? 0

  // The shared paginator returns totalCount = -1 when the count query times out
  // (common on large lists). Fall back to "the last page came back full" so
  // infinite scroll still advances instead of re-capping at the first page.
  const hasMore =
    totalCount < 0
      ? loadedItems.length > 0 && loadedItems.length % LIMIT === 0
      : loadedItems.length < totalCount

  useEffect(() => {
    if (inView && loadedItems.length > 0 && hasMore) {
      fetchMore({
        variables: {
          pageInfo: { offset: loadedItems.length, limit: LIMIT },
        },
      })
    }
  }, [inView, fetchMore, loadedItems.length, hasMore])

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-doubleNickel-gray-200 p-3 shadow-sm">
      <div className="mb-2 flex flex-row items-center justify-between text-base font-medium text-doubleNickel-gray-800">
        <div className="flex flex-row items-center gap-3">
          <div className="flex flex-row items-center gap-1.5">
            Expirations
            <Tooltip
              label="Shows documents and certifications that are expiring soon for hired employees."
              withArrow
              position="top"
              className="text-xs"
            >
              <div className="flex items-center">
                <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
              </div>
            </Tooltip>
          </div>
          <SegmentedControl
            size="xs"
            data={VIEW_OPTIONS}
            value={viewMode}
            onChange={handleViewModeChange}
            classNames={{ root: 'p-0.5', label: 'px-2.5 py-0.5 leading-5' }}
          />
        </div>
        <Box
          className="flex flex-row items-center gap-1 text-xs text-doubleNickel-gray-600 hover:underline"
          onClick={() =>
            onMetricClick('applicantStages', [ApplicantStage.HIRED])
          }
        >
          See all hired employees
          <IconRightArrow className="h-3 fill-none stroke-doubleNickel-gray-700" />
        </Box>
      </div>
      <div
        className={cn(
          'grid grid-cols-12 bg-doubleNickel-gray-50 px-2 py-1 text-xs font-semibold text-doubleNickel-gray-700'
        )}
      >
        <div className="col-span-4">Name</div>
        <div className="col-span-3">Category</div>
        <div className="col-span-3">Expiration</div>
        <div className="invisible col-span-1">placeholder</div>
        <div className="invisible col-span-1">placeholder</div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        {loadingExpiringDocuments &&
        networkStatus !== NetworkStatus.fetchMore ? (
          <div className="flex flex-1 flex-col items-center justify-center">
            <Loader size="sm" color="gray" />
          </div>
        ) : (
          <>
            {loadedItems.length > 0 &&
              loadedItems.map((document) => {
                  const category = document.category as DocumentCategory
                  const isDQF = isDQFCategory(category)
                  const stepType = mapDocumentCategoryToStepType(category)

                  // Build route params based on whether it's a DQF category
                  const routeParams: any = {
                    id: document.applicant.applicantId,
                    tab: 'documents',
                    documentId: document.documentId,
                  }

                  if (isDQF && stepType) {
                    // For DQF categories, use the dqf param with the correct StepType
                    routeParams.dqf = stepType
                  } else {
                    // For non-DQF categories, route to the documents subtab
                    routeParams.subtab = 'documents'
                  }

                  return (
                    <Link
                      key={document.documentId}
                      to={routes.applicantDetails(routeParams)}
                    >
                      <div
                        className={cn(
                          'border-bg-doubleNickel-gray-600 grid min-h-7 cursor-pointer grid-cols-12 items-center px-2 py-0.5 text-xs text-doubleNickel-gray-800 hover:bg-doubleNickel-gray-50'
                        )}
                      >
                        <div className="col-span-4 truncate">
                          {formatSnakeValue(document.applicant.fullName)}
                        </div>
                        <div className="col-span-3 truncate">
                          <Tooltip
                            label={document.fileName || ''}
                            withArrow
                            position="top"
                            className="text-xs"
                          >
                            <div className="truncate">
                              {stepType
                                ? formatStepType(stepType)
                                : formatStepType(document.category as StepType)}
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-span-3">
                          <ExpirationIndicator
                            expirationDate={document.expirationDate}
                            isTrackedCategory={isCategoryTracked(category)}
                          />
                        </div>
                        <div className="col-span-1 flex justify-center">
                          {getActiveTrackerId(document) && (
                            <CompanyFeatureGate feature="expirationTrackingEnabled">
                              <TrackerIconButton
                                trackerId={getActiveTrackerId(document)}
                                onClick={handleOpenTracker}
                              />
                            </CompanyFeatureGate>
                          )}
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Tooltip
                            label={`Go to ${formatSnakeValue(
                              document.applicant.fullName
                            )}'s document`}
                            withArrow
                            position="top"
                            className="text-xs"
                          >
                            <ActionIcon variant="subtle" color="gray">
                              <IconRightArrow className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
                            </ActionIcon>
                          </Tooltip>
                        </div>
                      </div>
                    </Link>
                  )
                })}
            {networkStatus === NetworkStatus.fetchMore && (
              <Loader
                size="sm"
                className="flex w-full flex-row justify-center py-2"
                color="gray"
              />
            )}
            {loadedItems.length > 0 && hasMore && (
              <div ref={ref} className="opacity-0">
                Inview placeholder
              </div>
            )}
            {networkStatus !== NetworkStatus.fetchMore &&
              !hasMore &&
              loadedItems.length > LIMIT && (
                <div className="flex w-full justify-center py-3 text-xs font-medium text-doubleNickel-gray-400">
                  No more documents to show
                </div>
              )}
          </>
        )}
        {!loadingExpiringDocuments && loadedItems.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-sm font-medium text-doubleNickel-gray-500">
            {viewMode === 'expiring'
              ? 'No expiring documents'
              : 'No documents with missing dates'}
          </div>
        )}
      </div>
      {trackerDrawerOpened && (
        <ExpirationDetailDrawer
          opened={trackerDrawerOpened}
          onClose={closeTrackerDrawer}
          trackerId={selectedTrackerId}
        />
      )}
    </div>
  )
}

export default DocumentChecklist
